import * as React from "react";

import { Box, Grid, Typography } from "@mui/material";

import CustomInput from "../../../helper-components/input/CustomInput";
import CustomButton from "../../../helper-components/input/CustomButton";
import CustomTooltip from "../../../helper-components/misc/CustomTooltip";
import CustomSelection from "../../../helper-components/form/CustomSelection";

import { querySelectLibraries } from "../../../helper-functions/api/libraryQueries";
import { getEllipsis } from "../../../helper-functions/shared/miscUtils";

import { validateRow } from "../../../helper-functions/validation/objectValidation";
import { addFormToObj, setInsertObjectState, setValue } from "../../../helper-functions/shared/miscUtils";
import { IS_SEARCH_SETTINGS_VALID, SEARCH_SETTINGS_VALIDATION } from "../../../helper-functions/validation/searchSettingsValidation";

import { getLanguage } from "../../../config/language/language";

/**
 * A form to update a author.
 */
export default function SearchSettingsForm(props) {
    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const validation = SEARCH_SETTINGS_VALIDATION;
    const isValid = IS_SEARCH_SETTINGS_VALID;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(existingRow))));

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow
        };

        setInsertObjectState(setRow, newRow);
    }, []);

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(row));
        setValue(newRow, key, value);

        validateRow(obj, validation, newRow);
        setInsertObjectState(setRow, newRow);
    };

    const handleClick = () => {
        if (!isValid(row)) {
            props.showSnackbar({ message: getLanguage().SNACKBAR_ERROR_FORM_INVALID, color: "error" });
            return;
        }

        const options = { row };
        callback(true, options);
    };

    return (
        <Box className="box">
            <Grid container spacing={2}>
                {/* <Grid item xs={12}>
                    <CustomInput name="mode" row={row} onChange={onChange} label="Search mode" select={["KEYWORD", "SEMANTIC"]} />
                </Grid> */}

                {row.mode === "KEYWORD" && (
                    <>
                        <Grid item xs={12}>
                            <CustomTooltip
                                form={
                                    <CustomInput
                                        name="keywordTable"
                                        row={row}
                                        onChange={onChange}
                                        label="Perform search on this table"
                                        select={["ALL", "RESOURCES", "TRANSCRIPTS", "QUOTES", "AUTHORS"]}
                                    />
                                }
                                tooltip="Select the table to perform the keyword search on. 'All' will search across all available tables."
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <CustomTooltip
                                form={<CustomInput name="keywordLimit" row={row} onChange={onChange} label="Maximum results to find" type="number" min={1} placeholder={"25"} />}
                                tooltip="Limits the number of results returned by the search."
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <CustomTooltip
                                form={
                                    <CustomInput
                                        name="keywordThreshold"
                                        row={row}
                                        onChange={onChange}
                                        label="Similarity threshold"
                                        type="number"
                                        step={0.01}
                                        min={0}
                                        max={1}
                                        placeholder={"0.03"}
                                    />
                                }
                                tooltip="Determines how similar the results must be to the query. A lower value means more results will be returned, while a higher value will filter out less relevant results."
                            />
                        </Grid>
                    </>
                )}

                {row.mode === "SEMANTIC" && (
                    <>
                        <Grid item xs={12} sm={6}>
                            <CustomTooltip
                                form={<CustomInput name="semanticLimit" row={row} onChange={onChange} label="Maximum results to find" type="number" min={1} placeholder={"10"} />}
                                tooltip="Limits the number of results returned by the search."
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <CustomTooltip
                                form={
                                    <CustomInput
                                        name="semanticMaxResultsPerResource"
                                        row={row}
                                        onChange={onChange}
                                        label="Maximum results per resource"
                                        type="number"
                                        min={1}
                                        placeholder={"3"}
                                    />
                                }
                                tooltip="Limits the number of results returned from each resource. Useful for avoiding being overwhelmed with too many results from one single source."
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <CustomTooltip
                                form={
                                    <CustomInput
                                        name="semanticThreshold"
                                        row={row}
                                        onChange={onChange}
                                        label="Similarity threshold"
                                        type="number"
                                        step={0.01}
                                        min={0}
                                        max={1}
                                        placeholder={"0.15"}
                                    />
                                }
                                tooltip="Determines how similar the results must be to the query. A lower value means more results will be returned, while a higher value will filter out less relevant results."
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 1 }}>{getLanguage().SEARCH_LIBRARIES_LABEL}</Typography>
                            <Typography variant="caption" display="block" sx={{ mb: 1 }}>{getLanguage().SEARCH_LIBRARIES_HELP}</Typography>
                            <CustomSelection
                                {...props}
                                multiselect
                                callback={(selected) => onChange(row, "semanticLibraryIds", selected)}
                                query={querySelectLibraries}
                                selected={row.semanticLibraryIds || []}
                                getPrimarySearchText={(el) => el?.name}
                                getSecondarySearchText={(el) => el?.description}
                                getPrimaryText={(el) => getEllipsis(el?.name, 50)}
                                getSecondaryText={(el) => getEllipsis(el?.description, 50)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <CustomTooltip
                                form={
                                    <CustomInput
                                        name="semanticMinDurationBetweenResults"
                                        row={row}
                                        onChange={onChange}
                                        label="Minimum duration between same-resource results (s)"
                                        type="number"
                                        min={0}
                                        placeholder={"300"}
                                    />
                                }
                                tooltip="This setting prevents returning results that are too close together in time from the same resource. This is useful for avoiding redundancy, as it ensures that results are spaced out by at least the specified number of seconds."
                            />
                        </Grid>
                    </>
                )}
            </Grid>

            <CustomButton {...props} title={"Save settings"} onClick={handleClick} className="button" fullWidth />
        </Box>
    );
}
